import { Link, Outlet } from 'react-router-dom';
import { ArrowLeft, Store } from 'lucide-react';
import VirtualAssistantProvider from './VirtualAssistantProvider';
import RingOverlay from './RingOverlay';

/**
 * CustomerPortalLayout — full-bleed frame for the Customer Portal.
 *
 * Owner directive 2026-09-30: the Customer Portal must open as its own screen
 * (full width, its own menu) instead of being nested inside the regular site
 * shell, where the site sidebar squeezed it.
 *
 * This layout renders OUTSIDE AppShell — no site sidebar and no site topbar.
 * It provides its own header (brand + "Back to app") plus the app-canvas
 * background, and the portal page below supplies its own tab bar. The URL stays
 * `/app/customer-portal*` so every existing link keeps working.
 *
 * The VirtualAssistantProvider is kept so the assistant, screen-report, and
 * ring overlay behave exactly as they do everywhere else in the app.
 */
export default function CustomerPortalLayout() {
  return (
    <VirtualAssistantProvider>
      <div className="app-canvas flex min-h-[100dvh] flex-col">
        <header
          className="glass-bar sticky top-0 z-30 box-border flex flex-none items-center justify-between gap-3 px-4 lg:px-6"
          style={{
            height: 'calc(4rem + env(safe-area-inset-top))',
            paddingTop: 'env(safe-area-inset-top)',
          }}
        >
          <div className="flex min-w-0 items-center gap-3">
            <Link
              to="/app"
              data-agent-id="portal.back"
              className="flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              <ArrowLeft size={16} aria-hidden="true" /> Back to app
            </Link>
            <div className="flex min-w-0 items-center gap-2">
              <div
                className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-purple-600 text-white shadow-lg shadow-brand-500/20"
                aria-hidden="true"
              >
                <Store size={16} />
              </div>
              <span className="truncate text-sm font-semibold text-slate-950 dark:text-slate-100">Customer Portal</span>
            </div>
          </div>
          <span className="hidden text-xs font-medium text-slate-500 sm:block">Owner console</span>
        </header>
        <main id="main-content" className="min-h-0 flex-1 overflow-y-auto overflow-x-clip p-4 lg:p-6" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
      {/* Keep the assistant ring visible on the portal screen too. */}
      <RingOverlay />
    </VirtualAssistantProvider>
  );
}
